import { useLayoutEffect, useRef, useState } from "react";

export const PAPERS = {
  letter: { label: "Letter", w: 215.9, h: 279.4, css: "letter" },
  a4: { label: "A4", w: 210, h: 297, css: "A4" },
  rollo: { label: 'Rollo 4×6"', w: 101.6, h: 152.4, css: "101.6mm 152.4mm", labelStock: true },
};
const CARD = { w: 105, h: 148 };
const PX_PER_MM = 96 / 25.4;
const MARK = 6; // cut-mark length (mm)
const GAP = 1.5; // distance from card edge (mm)
const EDGE = 2; // safe margin on label stock (mm)

// L-shaped cut marks just outside the four corners of the card.
const CutMarks = ({ left, top, w, h }) => {
  const corners = [
    ["top-left", left, top, -1, -1],
    ["top-right", left + w, top, 1, -1],
    ["bottom-left", left, top + h, -1, 1],
    ["bottom-right", left + w, top + h, 1, 1],
  ];
  return corners.map(([corner, x, y, dx, dy]) => (
    <g key={corner} stroke="#555" strokeWidth="0.2">
      <line x1={x} y1={y + dy * GAP} x2={x} y2={y + dy * (GAP + MARK)} />
      <line x1={x + dx * GAP} y1={y} x2={x + dx * (GAP + MARK)} y2={y} />
    </g>
  ));
};

const SHRINK = 1; // label stock: print 1 mm smaller than the page so Chrome never spills a hairline onto a 2nd label

// The paper sheet (Letter, A4 or a Rollo 4x6" label) with the A6 card centred — scaled to fit the stock, 1:1 when printed.
export const PassportSheet = ({ paper, children }) => {
  const { w: pw, h: ph, labelStock } = PAPERS[paper];
  const w = labelStock ? pw - SHRINK : pw;
  const h = labelStock ? ph - SHRINK : ph;
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  const pad = labelStock ? EDGE : 0;
  const cardScale = Math.min(1, (w - 2 * pad) / CARD.w, (h - 2 * pad) / CARD.h);
  const cw = CARD.w * cardScale;
  const ch = CARD.h * cardScale;
  const left = (w - cw) / 2;
  const top = (h - ch) / 2;

  useLayoutEffect(() => {
    const fit = () => setScale(Math.min(1, (wrapRef.current?.clientWidth || 9999) / (w * PX_PER_MM)));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [w]);

  return (
    <div ref={wrapRef} className="print-sheet-wrap relative w-full overflow-hidden" style={{ height: `${h * PX_PER_MM * scale}px` }}>
      <div
        data-testid="passport-sheet"
        className={`print-sheet print-exact absolute top-0 left-1/2 bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.35)] origin-top ${labelStock ? "print-label overflow-hidden" : ""}`}
        style={{ width: `${w}mm`, height: `${h}mm`, transform: `translateX(-50%) scale(${scale})` }}
      >
        {!labelStock && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
            <CutMarks left={left} top={top} w={cw} h={ch} />
          </svg>
        )}
        {/* `zoom` (not transform) so the card's LAYOUT box shrinks too — a transformed 148 mm box still paginates past a 6" label */}
        <div className="absolute" style={{ left: `${left}mm`, top: `${top}mm`, width: `${CARD.w}mm`, height: `${CARD.h}mm`, zoom: cardScale }}>
          {children}
        </div>
      </div>
    </div>
  );
};
